import { lazy, Suspense } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { AppBar } from './app-bar';
import { AddToHomeBar } from './add-to-home-bar';
import { SignedInAccountProvider } from './signed-in-account';
import { TabBar, tabBarVisible } from './tab-bar';
import { ToastProvider } from './toast';
import { useLanguage } from './language-context';
import { ChromeSprite } from './chrome-icons';
import './shell.css';
import './site-chrome.css';

// The footer is below the fold on every page, so it (and its newsletter form) loads in its
// own chunk rather than the entry bundle (INF-50 budget).
const SiteFooter = lazy(() => import('./site-footer').then((m) => ({ default: m.SiteFooter })));

// The two Listings pages (DR-81) draw their own <main> landmark and own the bottom edge
// on phones (no tab bar); the shared header and footer render on them like every page.
const LISTINGS_ROUTE = /^\/(?:search|business|b|l)(?:\/|$)/;
export function listingsRoute(pathname: string): boolean {
  return LISTINGS_ROUTE.test(pathname) && !/^\/(?:business|b)\/[^/]+\/book/.test(pathname);
}

// The persistent shell every screen renders into (CB-D2). The site header on top, the
// routed screen in the middle, the site footer below — the same header and footer on
// every website page at every width (Blog design export, owner's call 4 Nov 2026) — and
// on mobile the tab bar, its sheets and, on home, the add-to-home-screen bar.
// `data-tabbar` lets CSS reserve room for the tab bar and lift the toast above it
// (design `toastLift`) only on routes that show it.
export function AppLayout() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const lr = listingsRoute(pathname);
  return (
    <SignedInAccountProvider>
      <ToastProvider>
        <div className="app-shell" data-tabbar={tabBarVisible(pathname) || undefined}>
          {/* First focusable element on the page, so a keyboard user can skip the header
              instead of tabbing through it on every navigation (§12). */}
          <a className="skip-link" href="#main">
            {t('nav.skip_to_content')}
          </a>
          <ChromeSprite />
          <AppBar />
          {/* The Listings pages draw their own <main> (the design's landmark), so the
              shell's outlet is a plain container there — one main per page. */}
          {lr ? (
            <div className="app-main app-main--lr" id="main" tabIndex={-1}>
              <Outlet />
            </div>
          ) : (
            <main className="app-main" id="main" tabIndex={-1}>
              <Outlet />
            </main>
          )}
          <Suspense fallback={null}>
            <SiteFooter />
          </Suspense>
          {pathname === '/' && <AddToHomeBar />}
          {!lr && <TabBar />}
          <ScrollRestoration />
        </div>
      </ToastProvider>
    </SignedInAccountProvider>
  );
}
